import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { PlusIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm } from 'react-hook-form';

import {
  DLLFormat,
  DLL_FORMAT,
  DLL_PAGE_OBJECTIVE_CHAR_LIMIT,
  DLL_ENABLED_FORMATS,
  LP_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

import { Button } from '../components/Button';
import {
  RemoveButton,
  RemoveButtonVariant,
  SelectField,
  TextField,
} from '../components/form-fields';
import GenerateButton from '../components/GenerateButton';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import { GradeLevelDropdown } from '../components/Select';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { day as daysOfTheWeek } from '../constants/day';
import {
  DLL_CURRICULUM_REFERENCE,
  DLL_PAGE_DESCRIPTION,
} from '../constants/dll';
import CreditContext from '../context/credits/context';
import { useGeneratePause } from '../context/generate/provider';
import { DllProperties, PageKey } from '../enums/pageKey';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import dllSchema from '../schemas/dllSchema';
import { dllProperties } from '../types/page';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';

const defaultPremiumCreditCost = parseInt(
  process.env.NEXT_PUBLIC_DEFAULT_GENERATE_PREMIUM_COST,
);

export default function DLL(): JSX.Element {
  const { context: creditContext } = useContext(CreditContext);
  const { isUserPremium } = creditContext;

  const [objectiveSubLabel, setObjectiveSubLabel] = useState<string>(
    'At the end of the lesson, the students will be able to...',
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dayOfTheWeekObjective = useMemo(
    () =>
      daysOfTheWeek.map((day) => ({
        day,
        dayObjectives: null,
      })),
    [],
  );

  const defaultValues = {
    gradeLevel: getLocalStorageItem(DllProperties.gradeLevel),
    subject: getLocalStorageItem(DllProperties.subject),
    language: getLocalStorageItem(DllProperties.language, 'English'),
    format: getLocalStorageItem(DllProperties.format),
    objectives: dayOfTheWeekObjective,
  };

  const dllForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(dllSchema) as Resolver<{
      gradeLevel: string;
      subject: string;
      language: string;
      format: string;
      objectives: { day: string; dayObjectives: string[] | null }[];
    }>,
  });

  const {
    formState: { errors, touchedFields },
    setValue,
    watch,
    trigger,
  } = dllForm;

  const gradeLevel = watch('gradeLevel');
  const subject = watch('subject');
  const language = watch('language');
  const format = watch('format');
  const objectives = watch('objectives');

  const formContents = useMemo(
    () => ({
      [DllProperties.format]: format,
      [DllProperties.subject]: subject,
      [DllProperties.gradeLevel]: gradeLevel,
      [DllProperties.language]: language,
      [DllProperties.objectives]: objectives,
    }),
    [format, subject, gradeLevel, objectives, language],
  );

  useEffect(() => {
    const subLabelText =
      language === Language.ENGLISH
        ? 'At the end of the lesson, the students will be able to...'
        : 'Ang mag-aaral ay inaasahan na...';

    setObjectiveSubLabel(subLabelText);
  }, [language]);

  const triggerValidation = useCallback(
    async (fieldName) => {
      await trigger(fieldName);
    },
    [trigger],
  );

  const updateFieldValue = useCallback(
    async (fieldName, value) => {
      setValue(fieldName, value);
    },
    [setValue],
  );

  const customFormValidator = useCallback(async () => {
    await trigger();
  }, [trigger]);

  const hasNonEmptyObjectives = (dayObjectives: string[] | null): boolean => {
    return dayObjectives
      ? dayObjectives.some((objective) => objective.trim() !== '')
      : false;
  };

  const getNumberOfDaysObjectives = useCallback(() => {
    return objectives.filter((obj) => hasNonEmptyObjectives(obj.dayObjectives))
      .length;
  }, [objectives]);

  const calculateCreditCost = useMemo(() => {
    return getNumberOfDaysObjectives() <= 2
      ? defaultPremiumCreditCost
      : getNumberOfDaysObjectives();
  }, [getNumberOfDaysObjectives]);

  const handleAddObjective = useCallback(
    (index: number) => {
      if (!objectives) {
        return;
      }

      const newObjectives = [...objectives];
      newObjectives[index] = {
        day: objectives[index].day,
        dayObjectives: ['', '', ''],
      };
      setValue('objectives', newObjectives);
      trigger('objectives');
    },
    [objectives, setValue, trigger],
  );

  const handleRemoveObjective = useCallback(
    (index: number) => {
      const newObjectives = [...objectives];
      newObjectives[index] = {
        day: objectives[index].day,
        dayObjectives: null,
      };
      setValue('objectives', newObjectives);
      trigger('objectives');
    },
    [objectives, setValue, trigger],
  );

  const handleObjectiveChange = useCallback(
    (index: number, objIndex: number, value: string) => {
      const newObjectives = [...objectives];
      const newDayObjectives = [...(objectives[index].dayObjectives || [])];
      newDayObjectives[objIndex] = value;
      newObjectives[index] = {
        day: objectives[index].day,
        dayObjectives: newDayObjectives,
      };
      processInput(
        setValue,
        trigger as (fieldName: string) => void,
        'objectives',
        newObjectives,
      );
    },
    [objectives, setValue, trigger],
  );

  const banner = useMemo(
    () => (
      <GenericBanner
        content={DLL_PAGE_DESCRIPTION}
        isPremium={isUserPremium}
        link={process.env.NEXT_PUBLIC_TUTORIAL_LESSONPLAN}
        title="Daily Lesson Log"
      />
    ),
    [isUserPremium],
  );

  const isGradeLevelInvalid = touchedFields.gradeLevel && !gradeLevel;
  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.dailyLessonLog];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;
  const watchedValues = watch();
  const isObjectiveDisabled = (index: number, objIndex: number) => {
    const dayObjectives = objectives[index].dayObjectives;
    const currentObjective = dayObjectives?.[objIndex];
    const previousObjective = dayObjectives?.[objIndex - 1];
    return objIndex > 0 && !currentObjective && !previousObjective;
  };

  return (
    <MainContent
      banner={banner}
      creditCost={calculateCreditCost}
      formContents={formContents as dllProperties}
      pageKey={PageKey.dailyLessonLog}
      setIsLoading={setIsLoading}
    >
      <SelectField
        className="col-span-full"
        error={!!errors.format}
        errorMessage={errors.format?.message as string}
        id="format"
        label="Curriculum"
        link={DLL_CURRICULUM_REFERENCE}
        name="format"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.format}
      >
        {DLL_FORMAT.map(
          (format: DLLFormat, index) =>
            DLL_ENABLED_FORMATS.includes(format) && (
              <option key={index} value={format}>
                {format}
              </option>
            ),
        )}
      </SelectField>
      <TextField
        className="col-span-full"
        errorMessage={errors.subject?.message as string}
        id="subject"
        isMaxLengthReached={subject?.length >= LP_PAGE_SUBJECT_CHAR_LIMIT}
        label="Subject"
        maxLength={LP_PAGE_SUBJECT_CHAR_LIMIT}
        name="subject"
        placeholder="Input the subject for this lesson plan"
        type="subject"
        value={subject}
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            'subject',
            subject?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            'subject',
            e.target.value,
          );
        }}
      />
      <GradeLevelDropdown
        dllFormat={format as DLLFormat}
        error={!!errors.gradeLevel || isGradeLevelInvalid}
        errorMessage={errors.gradeLevel?.message as string}
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.gradeLevel}
      />
      <div className="col-span-full">
        <div className="block text-lg text-gray-700">Objectives:</div>
        <div className="mb-3 block text-base italic text-gray-700">
          {objectiveSubLabel}
        </div>
        {errors.objectives?.message && (
          <div className="mb-3 block text-base text-red-500">
            {errors.objectives.message}
          </div>
        )}
        {objectives.map((data, index) => (
          <div key={data.day} className="mb-6 p-5">
            <div className="flex justify-between">
              <div
                className={`flex items-center justify-between ${
                  data.dayObjectives ? 'w-full' : ''
                }`}
              >
                <label className="block text-lg text-gray-700">
                  {data.day}:
                </label>
                {data.dayObjectives && (
                  <RemoveButton
                    variant={RemoveButtonVariant.SECTION}
                    onClick={() => handleRemoveObjective(index)}
                  />
                )}
              </div>

              {!data.dayObjectives && (
                <div className="mr-8 flex justify-end">
                  <Button
                    className="disabled:bg-gray-00 disabled:text-gray inline-block text-sm font-medium text-blue-600 outline-none focus:outline-none disabled:cursor-not-allowed"
                    title="Click to add a new objective for the day."
                    type="button"
                    onClick={() => handleAddObjective(index)}
                  >
                    <PlusIcon className="mr-2 inline h-4 w-4" />
                    Add Objectives
                  </Button>
                </div>
              )}
            </div>
            {errors.objectives?.[index]?.dayObjectives?.message && (
              <span className="text-s mb-3 block text-red-500">
                {errors.objectives?.[index]?.dayObjectives?.message as string}
              </span>
            )}
            {data.dayObjectives?.map((objective, objIndex) => (
              <div key={objIndex} className="mb-2 mt-2">
                <TextField
                  disabled={isObjectiveDisabled(index, objIndex)}
                  id={`${data.day}-objective-${objIndex}`}
                  label={''}
                  maxLength={DLL_PAGE_OBJECTIVE_CHAR_LIMIT}
                  name={`${data.day}-objective-${objIndex}`}
                  placeholder={`Input Objective ${objIndex + 1}`}
                  type="topic"
                  value={objective}
                  onBlur={() => {
                    const newObjectives = [...objectives];
                    const trimmedValue = objective?.trim() || '';
                    newObjectives[index] = {
                      ...newObjectives[index],
                      dayObjectives: newObjectives[index].dayObjectives.map(
                        (obj, idx) => (idx === objIndex ? trimmedValue : obj),
                      ),
                    };
                    processInput(
                      setValue,
                      trigger as (fieldName: string) => void,
                      'objectives',
                      newObjectives,
                    );
                  }}
                  onChange={(e) =>
                    handleObjectiveChange(index, objIndex, e.target.value)
                  }
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <SelectField
        className="col-span-full"
        error={!!errors.language}
        errorMessage={errors.language?.message as string}
        id="language"
        label="Translation"
        name="language"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.language}
      >
        <option value={Language.ENGLISH}>{Language.ENGLISH}</option>
        <option value={Language.FILIPINO}>{Language.FILIPINO}</option>
      </SelectField>
      <div className="col-span-full">
        <div className="flex flex-col">
          <GenerateButton
            isPremium
            buttonText={buttonText}
            creditCost={calculateCreditCost}
            disabled={isGenerateButtonDisabled}
            onClick={customFormValidator}
          />
        </div>
      </div>
    </MainContent>
  );
}

DLL.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
